import { Link } from '@cedarjs/router'
import { MetaTags, useMutation, useQuery } from '@cedarjs/web'
import React from 'react'
import type {
  FindMonitoringAlerts,
  UpdateMonitoringAlert,
  UpdateMonitoringAlertInput,
} from 'types/graphql'

const QUERY = gql`
  query FindMonitoringAlerts {
    monitoringAlerts(status: OPEN) {
      id
      message
      status
      alertType
      eventTimestamp
      user {
        id
        firstName
        lastName
      }
      aircraft {
        id
        orgSlug
        tailNumber
      }
      complianceLedger {
        id
        aircraft {
          id
          orgSlug
          tailNumber
        }
      }
      internalWorkOrder {
        id
        aircraft {
          id
          orgSlug
          tailNumber
        }
      }
    }
  }
`
const UPDATE_MONITORING_ALERT = gql`
  mutation UpdateMonitoringAlert(
    $id: String!
    $input: UpdateMonitoringAlertInput!
  ) {
    updateMonitoringAlert(id: $id, input: $input) {
      id
      status
    }
  }
`
type AlertType = NonNullable<FindMonitoringAlerts['monitoringAlerts']>[number]

const AlertMessage: React.FC<{ alert: AlertType }> = ({ alert }) => {
  if (alert.alertType === 'USER_INACTIVITY' && alert.user) {
    return (
      <span>
        User{' '}
        <span className="font-semibold text-blue-600">
          {alert.user.firstName} {alert.user.lastName}
        </span>{' '}
        has been inactive.
      </span>
    )
  }

  if (alert.alertType === 'COMPLIANCE_LEDGER_NOT_CREATED' && alert.aircraft) {
    return (
      <span>
        Aircraft{' '}
        <Link
          to={`/admin/aircrafts/${alert.aircraft.id}`}
          className="font-semibold text-blue-600 hover:underline"
        >
          {alert.aircraft.tailNumber}
        </Link>{' '}
        has no recent compliance ledger.
      </span>
    )
  }

  if (
    alert.alertType === 'COMPLIANCE_LEDGER_STALLED' &&
    alert.complianceLedger
  ) {
    return (
      <span>
        Compliance Ledger on{' '}
        <Link
          to={`/admin/aircrafts/${alert.complianceLedger.aircraft.id}`}
          className="font-semibold text-blue-600 hover:underline"
        >
          {alert.complianceLedger.aircraft.tailNumber}
        </Link>{' '}
        is stalled.
      </span>
    )
  }

  if (
    alert.alertType === 'INTERNAL_WORK_ORDER_STALLED' &&
    alert.internalWorkOrder
  ) {
    return (
      <span>
        Work Order on{' '}
        <Link
          to={`/admin/aircrafts/${alert.internalWorkOrder.aircraft.id}`}
          className="font-semibold text-blue-600 hover:underline"
        >
          {alert.internalWorkOrder.aircraft.tailNumber}
        </Link>{' '}
        is stalled.
      </span>
    )
  }
  if (alert.alertType === 'INTERNAL_WORK_ORDER_NOT_CREATED' && alert.aircraft) {
    return (
      <span>
        Work Order on{' '}
        <Link
          to={`/admin/aircrafts/${alert.aircraft.id}`}
          className="font-semibold text-blue-600 hover:underline"
        >
          {alert.aircraft.tailNumber}
        </Link>{' '}
        is not created.
      </span>
    )
  }

  return <span>{alert.message}</span>
}

const AlertCategory: React.FC<{
  title: string
  alerts: AlertType[]
  onUpdate: (id: string, status: string) => void
}> = ({ title, alerts, onUpdate }) => {
  if (alerts.length === 0) return null

  const formattedTitle = title
    .replace(/_/g, ' ')
    .replace(
      /\w\S*/g,
      (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    )

  return (
    <div className="flex flex-col rounded-lg bg-white p-4 shadow-md">
      <h2 className="mb-2 flex-shrink-0 text-lg font-bold">{formattedTitle}</h2>
      <div className="overflow-auto" style={{ maxHeight: '60vh' }}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="sticky top-0 bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Details
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Since
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {alerts.map((alert) => {
              return (
                <tr key={alert.id}>
                  <td className="whitespace-nowrap px-4 py-2">
                    <AlertMessage alert={alert} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-500">
                    {new Date(alert.eventTimestamp).toLocaleDateString()}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2">
                    <button
                      onClick={() => onUpdate(alert.id, 'RESOLVED')}
                      className="text-sm text-indigo-600 hover:text-indigo-900"
                    >
                      Resolve
                    </button>
                    <button
                      onClick={() => onUpdate(alert.id, 'IGNORED')}
                      className="ml-2 text-sm text-gray-600 hover:text-gray-900"
                    >
                      Ignore
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const MonitoringDashboardPage = () => {
  const { data, loading, error } = useQuery<FindMonitoringAlerts>(QUERY)
  const [updateMonitoringAlert] = useMutation<
    UpdateMonitoringAlert,
    { id: string; input: UpdateMonitoringAlertInput }
  >(UPDATE_MONITORING_ALERT, {
    refetchQueries: [{ query: QUERY }],
  })

  const handleUpdateStatus = (id, status) => {
    updateMonitoringAlert({
      variables: { id, input: { status } },
    })
  }

  const alertsByType = (data?.monitoringAlerts ?? []).reduce((acc, alert) => {
    if (alert) {
      const type = alert.alertType
      ;(acc[type] = acc[type] || []).push(alert)
    }
    return acc
  }, {} as Record<string, AlertType[]>)

  if (loading) {
    return <div>Loading...</div>
  }

  if (error) {
    return <div style={{ color: 'red' }}>Error: {error.message}</div>
  }

  return (
    <>
      <MetaTags
        title="Monitoring Dashboard"
        description="Activity Monitoring Dashboard"
      />

      <div className="p-4">
        <h1 className="mb-4 text-2xl font-bold">
          Activity Monitoring Dashboard
        </h1>
        {data?.monitoringAlerts.length === 0 && (
          <div className="py-8 text-center">
            <h3 className="text-lg font-medium">All Systems Operational</h3>
            <p className="text-sm text-gray-500">
              There are no open monitoring alerts.
            </p>
          </div>
        )}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {alertsByType &&
            Object.entries(alertsByType).map(([type, alerts]) => (
              <AlertCategory
                key={type}
                title={type}
                alerts={alerts}
                onUpdate={handleUpdateStatus}
              />
            ))}
        </div>
      </div>
    </>
  )
}

export default MonitoringDashboardPage
